import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Spinner } from 'react-bootstrap';
import { GET_DASHBOARD_FAULTED_SITES_CHART } from 'actions/dashboard';
import { useDispatch, useSelector } from 'react-redux';
import Card from 'components/inputs/Card';
import * as _ from 'lodash';
import { Chart as ChartJS, CategoryScale, ArcElement, LinearScale, BarElement, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import moment from 'moment-timezone';
import FullScreenWrapper from './FullScreen';
import PropTypes from 'prop-types';

ChartJS.register(ArcElement, CategoryScale, LinearScale, PointElement, BarElement, LineElement, Title, Tooltip, Legend);

const FaultedSitesChart = ({ fullScreen, setFullScreen }) => {
  const dispatch = useDispatch();
  const barChartRef = useRef(null);

  const dashboardFaultedSitesData = useSelector((state) => state.dashboardFaultedSites.dashboardFaultedSites);
  const isLoader = useSelector((state) => state.dashboardFaultedSites.isLoading);

  const getDashboardFaultedSites = useCallback(
    (values) => {
      const data = {
        ...values,
        from: moment().tz('Asia/Dubai').subtract(1, 'day').startOf('day'),
        to: moment().tz('Asia/Dubai').subtract(1, 'day').endOf('day'),
      };
      dispatch({ type: GET_DASHBOARD_FAULTED_SITES_CHART, payload: data });
    },
    [dispatch]
  );

  useEffect(() => {
    getDashboardFaultedSites();
  }, []);

  const sortedData = _.orderBy(dashboardFaultedSitesData, (item) => item.total_session_count || 0, 'desc');

  const faultedSites = useMemo(() => {
    return {
      labels: _.map(sortedData, (item) => _.get(item, 'stationId.code', '')),
      datasets: [
        {
          label: 'Success Sessions',
          data: _.map(sortedData, (item) => _.get(item, 'success_sessions_count', '')),
          backgroundColor: '#14AC7F',
          barThickness: 15,
        },
        {
          label: 'Failed Sessions',
          data: _.map(sortedData, (item) => _.get(item, 'faulted_sessions_count', '')),
          backgroundColor: '#be210b',
          barThickness: 15,
        },
      ],
    };
  });

  const downloadBarGraph = () => {
    if (barChartRef.current) {
      const chartInstance = barChartRef.current;
      const originalCanvas = chartInstance.canvas;

      const tempCanvas = document.createElement('canvas');
      const extraHeight = 60;
      tempCanvas.width = originalCanvas.width;
      tempCanvas.height = originalCanvas.height + extraHeight;

      const ctx = tempCanvas.getContext('2d');

      ctx.fillStyle = '#1b2a38';
      ctx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);

      const combinedTextElement = document.querySelector('.dashboard-faultedSites_name');
      const [graphTitle, dateInfo] = combinedTextElement ? combinedTextElement.innerText.split('\n') : ['Bar Chart', ''];

      ctx.font = '22px Arial';
      ctx.fillStyle = 'white';
      ctx.textAlign = 'center';

      ctx.fillText(graphTitle, tempCanvas.width / 2, 20);

      ctx.font = '17px Arial';
      ctx.fillStyle = '#999';
      ctx.fillText(dateInfo, tempCanvas.width / 2, 50);

      ctx.drawImage(originalCanvas, 0, extraHeight);

      const imageURL = tempCanvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.href = imageURL;
      link.download = `${graphTitle.replace(/\s+/g, '_')}_${dateInfo.replace(/[^\w\d_-]/g, '')}.png`;
      link.click();
    }
  };

  return (
    <React.Fragment>
      <Card>
        <FullScreenWrapper isFullScreen={fullScreen} setIsFullScreen={setFullScreen} downloadCharts={downloadBarGraph}>
          <div className="dashboard-faultedSites_chart_main">
            <div className="dashboard-faultedSites_chart_inner">
              <div className="dashboard-faultedSites_chart_block">
                <div
                  className="dashboard-faultedSites_name"
                  style={{
                    margin: fullScreen ? '0 1vw' : '',
                  }}
                >
                  Top 10 Sites by Session Count
                  <span className="chart-date-info">{moment().tz('Asia/Dubai').subtract(1, 'day').format('DD/MM/YYYY')}</span>
                </div>
              </div>
            </div>
            <div className="dashboard-faultedSites_chart-img">
              {isLoader ? (
                <div className="loader--block">
                  <Spinner as="span" animation="border" size="sm" role="status" />
                </div>
              ) : (
                <div
                  className="faultedSites_chart-img"
                  style={{
                    flexGrow: '1',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    height: fullScreen ? 'calc(100vh - 100px)' : 'calc(90vh - 210px)',
                    margin: fullScreen ? '0 1vw' : '0',
                  }}
                >
                  <Bar
                    ref={barChartRef}
                    data={faultedSites}
                    options={{
                      scales: {
                        y: {
                          beginAtZero: true,
                          stacked: true,
                        },
                        x: {
                          stacked: true,
                        },
                      },
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          position: 'bottom',
                          labels: {
                            color: '#ffffff',
                          },
                        },
                        tooltip: {
                          callbacks: {
                            title: (tooltipItems) => {
                              const index = tooltipItems[0].dataIndex;
                              const item = sortedData[index];
                              return `Station Code: ${_.get(item, 'stationId.code', '-')}`;
                            },
                            label: (tooltipItem) => {
                              const index = tooltipItem.dataIndex;
                              const item = sortedData[index];
                              if (tooltipItem.dataset.label === 'Success Sessions') {
                                return `Successful Sessions: ${item.success_sessions_count || 0}`;
                              } else if (tooltipItem.dataset.label === 'Failed Sessions') {
                                return `Failed Sessions: ${item.faulted_sessions_count || 0}`;
                              }
                            },
                            afterBody: (tooltipItems) => {
                              const index = tooltipItems[0].dataIndex;
                              const item = sortedData[index];
                              const total = (item.success_sessions_count || 0) + (item.faulted_sessions_count || 0);
                              return `Total Sessions: ${total}`;
                            },
                          },
                        },
                      },
                    }}
                    height="100%"
                  />
                </div>
              )}
            </div>
          </div>
        </FullScreenWrapper>
      </Card>
    </React.Fragment>
  );
};

FaultedSitesChart.propTypes = {
  fullScreen: PropTypes.bool,
  setFullScreen: PropTypes.func,
};

export default FaultedSitesChart;
